import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    AlertTriangle,
    ShieldCheck,
    Mail,
    Wand2,
    Ban,
    Check,
    ArrowUp,
    ArrowDown,
    UserMinus,
    Chrome,
    KeyRound,
    Shield,
    Activity,
    Boxes,
    Box,
    Trash2,
} from "lucide-react";
import Tabs from "../../components/primitives/Tabs";
import DataTable from "../../components/primitives/DataTable";
import TextButton from "../../components/primitives/TextButton";
import IconButton from "../../components/primitives/IconButton";
import ConfirmDialog from "../../components/primitives/ConfirmDialog";
import AuditEventRow from "../../components/primitives/AuditEventRow";
import { admin, ApiError } from "../../services/api";
import { useAuth } from "../../services/AuthContext";
import { relativeTime } from "../../utils/auditFormat";
import "../../styles/admin-shell.css";
import "./AdminUserDetailPage.css";

const ACTION_DEFS = {
    verify: {
        label: "Verify email",
        icon: ShieldCheck,
        method: "verifyEmail",
        confirm: false,
        successMsg: "Email marked verified.",
    },
    sendReset: {
        label: "Send password reset",
        icon: KeyRound,
        method: "sendPasswordReset",
        confirm: false,
        successMsg: "Password reset link sent.",
    },
    sendMagic: {
        label: "Send magic link",
        icon: Wand2,
        method: "sendMagicLink",
        confirm: false,
        successMsg: "Magic link sent.",
    },
    disable: {
        label: "Disable",
        icon: Ban,
        method: "disableUser",
        confirm: true,
        variant: "danger",
        title: "Disable account",
        message: "The user will be signed out and locked out of the app.",
        successMsg: "Account disabled.",
    },
    enable: {
        label: "Enable",
        icon: Check,
        method: "enableUser",
        confirm: true,
        variant: "primary",
        title: "Re-enable account",
        message: "The user will be able to sign in again.",
        successMsg: "Account re-enabled.",
    },
    promote: {
        label: "Promote",
        icon: ArrowUp,
        method: "promoteUser",
        confirm: true,
        variant: "primary",
        title: "Promote to admin",
        message: "The user will gain full admin access.",
        successMsg: "User promoted to admin.",
    },
    demote: {
        label: "Demote",
        icon: ArrowDown,
        method: "demoteUser",
        confirm: true,
        variant: "danger",
        title: "Demote from admin",
        message: "The user will lose admin access.",
        successMsg: "User demoted.",
    },
    tierFree: {
        label: "Set Free",
        icon: ArrowDown,
        method: "setUserTier",
        tier: "free",
        confirm: true,
        variant: "danger",
        title: "Move to Free tier",
        message:
            "Their monthly pack allowance drops to the Free limit immediately. Usage already spent this month still counts.",
        successMsg: "Moved to Free.",
    },
    tierCore: {
        label: "Set Core",
        icon: ArrowUp,
        method: "setUserTier",
        tier: "core",
        confirm: true,
        variant: "primary",
        title: "Move to Core tier",
        message: "Raises their monthly pack allowance to the Core limit.",
        successMsg: "Moved to Core.",
    },
    tierPro: {
        label: "Set Pro",
        icon: ArrowUp,
        method: "setUserTier",
        tier: "pro",
        confirm: true,
        variant: "primary",
        title: "Move to Pro tier",
        message: "Pro has no monthly pack limit.",
        successMsg: "Moved to Pro.",
    },
    anonymise: {
        label: "Anonymise",
        icon: UserMinus,
        method: "anonymiseUser",
        confirm: true,
        variant: "danger",
        title: "Anonymise user (GDPR)",
        message:
            "Email, name, and avatar will be blanked. Their SKUs will be archived. Audit history is preserved. This cannot be undone.",
        successMsg: "User anonymised.",
    },
};

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString();
}

function AdminUserDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: me } = useAuth();
    const isSelf = me?.id === id;

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [tab, setTab] = useState("activity");

    const [activeAction, setActiveAction] = useState(null); // key in ACTION_DEFS
    const [actionError, setActionError] = useState(null);
    const [toast, setToast] = useState(null);

    const refresh = useCallback(async () => {
        const data = await admin.getUser(id);
        setUser(data);
        return data;
    }, [id]);

    useEffect(() => {
        let cancelled = false;
        const ctrl = new AbortController();
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const data = await admin.getUser(id, { signal: ctrl.signal });
                if (!cancelled) setUser(data);
            } catch (err) {
                if (cancelled || err?.name === "AbortError") return;
                setError(err instanceof ApiError ? err.message : err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            ctrl.abort();
        };
    }, [id]);

    const runAction = async (actionKey, reason) => {
        setActionError(null);
        const def = ACTION_DEFS[actionKey];
        if (!def) return;
        try {
            const fn = admin[def.method];
            if (def.tier) {
                await fn(id, def.tier, reason);
            } else if (def.confirm) {
                await fn(id, reason);
            } else {
                await fn(id);
            }
            setActiveAction(null);
            setToast({ msg: def.successMsg, tone: "success" });
            await refresh();
        } catch (err) {
            const msg =
                err instanceof ApiError
                    ? err.message
                    : err?.message || "Action failed";
            if (def.confirm) {
                setActionError(msg);
            } else {
                setToast({ msg, tone: "danger" });
                setActiveAction(null);
            }
        }
    };

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3500);
        return () => clearTimeout(t);
    }, [toast]);

    const actionDef = activeAction ? ACTION_DEFS[activeAction] : null;

    const tabsList = useMemo(
        () => [
            { value: "activity", label: "Activity", icon: Activity },
            {
                value: "skus",
                label: "SKUs",
                icon: Boxes,
                count: user?.sku_count ?? undefined,
            },
            {
                value: "packs",
                label: "Packs",
                icon: Box,
                count: user?.pack_count ?? undefined,
            },
        ],
        [user]
    );

    return (
        <div className="admin-page admin-detail-page">
            <header className="admin-page__header admin-detail__header">
                <div className="admin-page__title-block">
                    <Link to="/admin/users" className="admin-detail__back">
                        <ArrowLeft size={12} aria-hidden="true" />
                        Users
                    </Link>
                    <span className="admin-page__eyebrow uppercase-label">
                        Admin / User
                    </span>
                    {loading && !user ? (
                        <h1 className="admin-page__title">Loading…</h1>
                    ) : user ? (
                        <UserHeader user={user} />
                    ) : (
                        <h1 className="admin-page__title">Not found</h1>
                    )}
                </div>
            </header>

            <div className="admin-page__body">
                {error && (
                    <div className="admin-error" role="alert">
                        <AlertTriangle size={14} aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}

                {toast && (
                    <div className={`admin-toast admin-toast--${toast.tone}`} role="status">
                        {toast.msg}
                    </div>
                )}

                {user && (
                    <ActionRow
                        user={user}
                        isSelf={isSelf}
                        onAction={(key) => {
                            setActionError(null);
                            const def = ACTION_DEFS[key];
                            if (def.confirm) {
                                setActiveAction(key);
                            } else {
                                runAction(key);
                            }
                        }}
                    />
                )}

                {user && (
                    <>
                        <Tabs
                            tabs={tabsList}
                            value={tab}
                            onChange={setTab}
                            ariaLabel="User detail sections"
                        />

                        {tab === "activity" && <ActivityTab userId={id} />}
                        {tab === "skus" && <SkusTab userId={id} setToast={setToast} />}
                        {tab === "packs" && <PacksTab userId={id} navigate={navigate} />}
                    </>
                )}
            </div>

            <ConfirmDialog
                open={!!activeAction && actionDef?.confirm === true}
                eyebrow="Admin action"
                title={actionDef?.title}
                message={actionDef?.message}
                confirmLabel={actionDef?.label}
                variant={actionDef?.variant || "danger"}
                requireReason
                error={actionError}
                onConfirm={(reason) => runAction(activeAction, reason)}
                onClose={() => {
                    setActiveAction(null);
                    setActionError(null);
                }}
            />
        </div>
    );
}

function UserHeader({ user }) {
    const initial = (user.name || user.email || "?").charAt(0).toUpperCase();
    return (
        <div className="user-header">
            {user.picture_url ? (
                <img
                    className="user-header__avatar"
                    src={user.picture_url}
                    alt=""
                    referrerPolicy="no-referrer"
                />
            ) : (
                <div
                    className="user-header__avatar user-header__avatar--fallback"
                    aria-hidden="true"
                >
                    {initial}
                </div>
            )}
            <div className="user-header__text">
                <h1 className="admin-page__title user-header__name">
                    {user.name || user.email || "Unnamed"}
                </h1>
                <div className="user-header__email mono">
                    {user.is_anonymized
                        ? "(anonymised)"
                        : user.email || "—"}
                </div>
                <div className="user-header__badges">
                    {user.has_google && (
                        <span className="users__badge">
                            <Chrome size={10} aria-hidden="true" />
                            Google
                        </span>
                    )}
                    {user.has_password && (
                        <span className="users__badge">
                            <KeyRound size={10} aria-hidden="true" />
                            Password
                        </span>
                    )}
                    {user.is_admin && (
                        <span className="users__badge users__badge--accent">
                            <Shield size={10} aria-hidden="true" />
                            Admin
                        </span>
                    )}
                    <span
                        className={`users__badge ${
                            user.tier && user.tier !== "free"
                                ? "users__badge--accent"
                                : "users__badge--muted"
                        }`}
                        title="Subscription tier"
                    >
                        {(user.tier || "free").toUpperCase()}
                    </span>
                    {user.is_email_verified ? (
                        <span className="users__badge users__badge--success">
                            <Check size={10} aria-hidden="true" />
                            Verified
                        </span>
                    ) : (
                        <span className="users__badge users__badge--muted">
                            Unverified
                        </span>
                    )}
                    {user.is_disabled && (
                        <span className="users__badge users__badge--danger">
                            <Ban size={10} aria-hidden="true" />
                            Disabled
                        </span>
                    )}
                    {user.is_anonymized && (
                        <span className="users__badge users__badge--muted">
                            <UserMinus size={10} aria-hidden="true" />
                            Anonymised
                        </span>
                    )}
                </div>
                <div className="user-header__meta">
                    <span>
                        <span className="uppercase-label">Created</span>{" "}
                        <span className="mono">{fmtDate(user.created_at)}</span>
                    </span>
                    <span>
                        <span className="uppercase-label">Last login</span>{" "}
                        <span className="mono">
                            {user.last_login_at
                                ? fmtDate(user.last_login_at)
                                : "Never"}
                        </span>
                    </span>
                    {user.email_verified_at && (
                        <span>
                            <span className="uppercase-label">Verified at</span>{" "}
                            <span className="mono">{fmtDate(user.email_verified_at)}</span>
                        </span>
                    )}
                    {user.disabled_at && (
                        <span>
                            <span className="uppercase-label">Disabled at</span>{" "}
                            <span className="mono">{fmtDate(user.disabled_at)}</span>
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function ActionRow({ user, isSelf, onAction }) {
    const cant = (key) => {
        if (isSelf && (key === "disable" || key === "demote" || key === "anonymise")) {
            return "Admins can't run this on themselves.";
        }
        if (key === "enable" && !user.is_disabled) return "User isn't disabled.";
        if (key === "disable" && user.is_disabled) return "Already disabled.";
        if (key === "promote" && user.is_admin) return "Already an admin.";
        if (key === "demote" && !user.is_admin) return "Not an admin.";
        if (key === "verify" && user.is_email_verified) return "Already verified.";
        if (key === "tierFree" && user.tier === "free") return "Already on Free.";
        if (key === "tierCore" && user.tier === "core") return "Already on Core.";
        if (key === "tierPro" && user.tier === "pro") return "Already on Pro.";
        if (key === "sendReset" && !user.has_password)
            return "User has no password to reset.";
        if (user.is_anonymized) return "Anonymised users can't be modified.";
        return null;
    };

    const btn = (key) => {
        const def = ACTION_DEFS[key];
        const disabled = !!cant(key);
        return (
            <TextButton
                key={key}
                size="sm"
                variant={def.variant === "danger" ? "danger" : "default"}
                icon={def.icon}
                onClick={() => onAction(key)}
                disabled={disabled}
                title={cant(key) || def.label}
            >
                {def.label}
            </TextButton>
        );
    };

    return (
        <div className="user-actions">
            {!user.is_email_verified && btn("verify")}
            {btn("sendReset")}
            {btn("sendMagic")}
            {!user.is_disabled ? btn("disable") : btn("enable")}
            {!user.is_admin ? btn("promote") : btn("demote")}
            {btn("tierFree")}
            {btn("tierCore")}
            {btn("tierPro")}
            {btn("anonymise")}
        </div>
    );
}

function ActivityTab({ userId }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState(null);

    const PAGE = 50;

    useEffect(() => {
        let cancelled = false;
        const ctrl = new AbortController();
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const res = await admin.listUserEvents(
                    userId,
                    { limit: PAGE, offset },
                    { signal: ctrl.signal }
                );
                if (!cancelled) {
                    setEvents(res.events || []);
                    setTotal(res.total || 0);
                }
            } catch (err) {
                if (cancelled || err?.name === "AbortError") return;
                setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            ctrl.abort();
        };
    }, [userId, offset]);

    const showingFrom = events.length === 0 ? 0 : offset + 1;
    const showingTo = offset + events.length;
    const hasNext = offset + PAGE < total;
    const hasPrev = offset > 0;

    return (
        <div className="admin-feed">
            {error && (
                <div className="admin-error" role="alert">
                    <AlertTriangle size={14} aria-hidden="true" />
                    <span>{error}</span>
                </div>
            )}
            {loading && events.length === 0 ? (
                <div className="admin-loading">Loading activity…</div>
            ) : events.length === 0 ? (
                <div className="admin-empty mono">No activity recorded.</div>
            ) : (
                <div className="admin-feed__list">
                    {events.map((ev) => (
                        <AuditEventRow key={ev.id} event={ev} />
                    ))}
                </div>
            )}
            <div className="admin-pagination">
                <span>
                    {total === 0 ? "0 events" : `${showingFrom}–${showingTo} of ${total}`}
                </span>
                <div className="admin-pagination__buttons">
                    <TextButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setOffset(Math.max(0, offset - PAGE))}
                        disabled={!hasPrev || loading}
                    >
                        Prev
                    </TextButton>
                    <TextButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setOffset(offset + PAGE)}
                        disabled={!hasNext || loading}
                    >
                        Next
                    </TextButton>
                </div>
            </div>
        </div>
    );
}

function SkusTab({ userId, setToast }) {
    const [skus, setSkus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirming, setConfirming] = useState(null); // { id, name }
    const [confirmErr, setConfirmErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await admin.listUserSkus(userId);
            setSkus(res.skus || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        load();
    }, [load]);

    const onDelete = async (reason) => {
        if (!confirming) return;
        setConfirmErr(null);
        try {
            await admin.deleteSku(confirming.id, reason);
            setConfirming(null);
            setToast?.({ msg: "SKU hard-deleted.", tone: "success" });
            load();
        } catch (err) {
            setConfirmErr(err instanceof ApiError ? err.message : err.message);
        }
    };

    const columns = useMemo(
        () => [
            {
                key: "name",
                label: "Name",
                width: "minmax(220px, 2fr)",
                render: (s) => (
                    <span className="users__email-cell">
                        <span className="users__email">{s.name}</span>
                        {s.sku_code && (
                            <span className="users__name mono">{s.sku_code}</span>
                        )}
                    </span>
                ),
            },
            {
                key: "dims",
                label: "W × H × D",
                width: "minmax(140px, 1fr)",
                render: (s) => (
                    <span className="mono-num">
                        {s.width} × {s.height} × {s.depth}
                    </span>
                ),
            },
            {
                key: "weight",
                label: "Weight",
                width: "minmax(80px, 0.6fr)",
                render: (s) =>
                    s.weight != null ? (
                        <span className="mono-num">{s.weight}</span>
                    ) : (
                        <span className="users__empty">—</span>
                    ),
            },
            {
                key: "status",
                label: "Status",
                width: "minmax(100px, 0.6fr)",
                render: (s) =>
                    s.archived_at || s.archived ? (
                        <span className="users__badge users__badge--muted">
                            Archived
                        </span>
                    ) : (
                        <span className="users__badge users__badge--success">
                            <Check size={10} aria-hidden="true" />
                            Active
                        </span>
                    ),
            },
            {
                key: "updated",
                label: "Updated",
                width: "minmax(120px, 0.7fr)",
                render: (s) => (
                    <span className="mono">{relativeTime(s.updated_at)}</span>
                ),
            },
            {
                key: "actions",
                label: "",
                width: "60px",
                render: (s) => (
                    <span className="users__actions">
                        <IconButton
                            icon={Trash2}
                            label={`Hard-delete ${s.name}`}
                            size="sm"
                            tone="danger"
                            onClick={(e) => {
                                e?.stopPropagation?.();
                                setConfirming({ id: s.id, name: s.name });
                            }}
                        />
                    </span>
                ),
            },
        ],
        []
    );

    return (
        <div className="admin-feed">
            {error && (
                <div className="admin-error" role="alert">
                    <AlertTriangle size={14} aria-hidden="true" />
                    <span>{error}</span>
                </div>
            )}
            <DataTable
                columns={columns}
                rows={skus}
                loading={loading}
                empty="No SKUs."
            />
            <ConfirmDialog
                open={!!confirming}
                eyebrow="Admin · SKU"
                title="Hard-delete SKU"
                message={
                    confirming
                        ? `Permanently remove "${confirming.name}". This is different from the user's own archive — it's irrecoverable.`
                        : ""
                }
                confirmLabel="Hard-delete"
                variant="danger"
                requireReason
                error={confirmErr}
                onConfirm={onDelete}
                onClose={() => {
                    setConfirming(null);
                    setConfirmErr(null);
                }}
            />
        </div>
    );
}

function PacksTab({ userId, navigate }) {
    const [data, setData] = useState({ jobs: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [error, setError] = useState(null);

    const PAGE = 50;

    useEffect(() => {
        let cancelled = false;
        const ctrl = new AbortController();
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const res = await admin.listUserPacks(
                    userId,
                    { limit: PAGE, offset },
                    { signal: ctrl.signal }
                );
                if (!cancelled) setData(res);
            } catch (err) {
                if (cancelled || err?.name === "AbortError") return;
                setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            ctrl.abort();
        };
    }, [userId, offset]);

    const columns = useMemo(
        () => [
            {
                key: "endpoint",
                label: "Endpoint",
                width: "minmax(140px, 0.8fr)",
                render: (j) => (
                    <span className="mono">
                        {j.endpoint === "pack/multi" ? "manifest" : "single"}
                    </span>
                ),
            },
            {
                key: "fitted_count",
                label: "Fitted",
                width: "minmax(80px, 0.5fr)",
                render: (j) =>
                    j.fitted_count != null ? (
                        <span className="mono-num">{j.fitted_count}</span>
                    ) : (
                        <span className="users__empty">—</span>
                    ),
            },
            {
                key: "duration_ms",
                label: "Duration",
                width: "minmax(80px, 0.5fr)",
                render: (j) =>
                    j.duration_ms != null ? (
                        <span className="mono-num">
                            {j.duration_ms}
                            <span className="users__empty"> ms</span>
                        </span>
                    ) : (
                        <span className="users__empty">—</span>
                    ),
            },
            {
                key: "error_code",
                label: "Status",
                width: "minmax(140px, 0.8fr)",
                render: (j) =>
                    j.error_code ? (
                        <span className="users__badge users__badge--danger">
                            {j.error_code}
                        </span>
                    ) : (
                        <span className="users__badge users__badge--success">
                            <Check size={10} aria-hidden="true" />
                            OK
                        </span>
                    ),
            },
            {
                key: "created_at",
                label: "When",
                width: "minmax(120px, 0.7fr)",
                render: (j) => (
                    <span className="mono">{relativeTime(j.created_at)}</span>
                ),
            },
        ],
        []
    );

    const total = data.total || 0;
    const showingFrom = data.jobs.length === 0 ? 0 : offset + 1;
    const showingTo = offset + data.jobs.length;
    const hasNext = offset + PAGE < total;
    const hasPrev = offset > 0;

    return (
        <div className="admin-feed">
            {error && (
                <div className="admin-error" role="alert">
                    <AlertTriangle size={14} aria-hidden="true" />
                    <span>{error}</span>
                </div>
            )}
            <DataTable
                columns={columns}
                rows={data.jobs}
                loading={loading}
                empty="No pack jobs."
                onRowClick={(j) => navigate(`/admin/packs/${j.id}`)}
            />
            <div className="admin-pagination">
                <span>
                    {total === 0 ? "0 packs" : `${showingFrom}–${showingTo} of ${total}`}
                </span>
                <div className="admin-pagination__buttons">
                    <TextButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setOffset(Math.max(0, offset - PAGE))}
                        disabled={!hasPrev || loading}
                    >
                        Prev
                    </TextButton>
                    <TextButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setOffset(offset + PAGE)}
                        disabled={!hasNext || loading}
                    >
                        Next
                    </TextButton>
                </div>
            </div>
        </div>
    );
}

export default AdminUserDetailPage;
